import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deleteEvent, listMyEvents } from "../../firebase/db.js";
import { PUBLIC_BASE_URL } from "../../firebase/config.js";
import { useAuth } from "../auth/AuthContext.jsx";
import SignIn from "../auth/SignIn.jsx";
import { trackMetaPixelPageView } from "../../external/metaPixel.js";

function ButtonIcon({ children }) {
  return <span className="dashboard-button-icon" aria-hidden="true">{children}</span>;
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3.25v9.5" />
      <path d="M3.25 8h9.5" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.25 3.25h6.5v6.5" />
      <path d="M12.25 3.75 6 10" />
      <path d="M12.75 8.25v3a1.5 1.5 0 0 1-1.5 1.5h-6.5a1.5 1.5 0 0 1-1.5-1.5v-6.5a1.5 1.5 0 0 1 1.5-1.5h3" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5.25" y="3.25" width="7.5" height="9.5" rx="1.5" />
      <path d="M3.25 10.25v-6.5a1.5 1.5 0 0 1 1.5-1.5h5.5" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m11.75 2.75 1.5 1.5a1.06 1.06 0 0 1 0 1.5l-6.9 6.9-2.6.6.6-2.6 6.9-6.9a1.06 1.06 0 0 1 1.5 0Z" />
      <path d="m10.75 3.75 1.5 1.5" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.25" y="3.75" width="9.5" height="9" rx="1.5" />
      <path d="M6 3.75h4" />
      <path d="M6.25 6.75h3.5" />
      <path d="M6.25 9h3.5" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.75 7.25a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
      <path d="M10.75 7.75a1.75 1.75 0 1 0 0-3.5" />
      <path d="M2.75 12.75a3 3 0 0 1 6 0" />
      <path d="M9.25 12.75a2.5 2.5 0 0 1 4 0" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.75 4.25h10.5" />
      <path d="M6.25 2.75h3.5" />
      <path d="m4.25 4.25.6 8a1.5 1.5 0 0 0 1.5 1.4h3.3a1.5 1.5 0 0 0 1.5-1.4l.6-8" />
      <path d="M6.5 6.5v4.5" />
      <path d="M9.5 6.5v4.5" />
    </svg>
  );
}

// "My events" — gated by auth. Lists the signed-in user's events.
export default function Dashboard() {
  const { user, loading, configured } = useAuth();
  const [events, setEvents] = useState(null);
  const [deletingSlug, setDeletingSlug] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  async function loadEvents() {
    try {
      setError(null);
      setEvents(await listMyEvents());
    } catch {
      setError("We couldn't load your events right now.");
      setEvents([]);
    }
  }

  useEffect(() => {
    if (!configured || !user) return;
    loadEvents();
  }, [user, configured]);

  useEffect(() => {
    trackMetaPixelPageView();
  }, []);

  async function handleDelete(slug) {
    if (!window.confirm(`Delete "${slug}"? This will permanently remove the event and its responses.`)) return;
    setDeletingSlug(slug);
    setError(null);
    try {
      await deleteEvent(slug);
      await loadEvents();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingSlug(null);
    }
  }

  async function handleCopyLink(slug) {
    const url = `${PUBLIC_BASE_URL}/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      window.setTimeout(() => {
        setCopiedSlug((current) => (current === slug ? null : current));
      }, 2000);
    } catch {
      setError("We couldn't copy that link right now.");
    }
  }

  const eventCount = events?.length ?? 0;
  const atLimit = eventCount >= 3;

  if (loading) return <div className="container" style={{ paddingTop: 80 }}>Loading…</div>;
  if (!user) return <SignIn />;

  return (
    <div className="container" style={{ paddingTop: 48, paddingBottom: 80 }}>
      <div className="row">
        <div>
          <h1 style={{ margin: 0 }}>My events</h1>
          <p className="muted" style={{ margin: "8px 0 0" }}>{eventCount}/3 events used</p>
        </div>
        <div className="spacer" />
        <button className="btn" disabled={atLimit} onClick={() => navigate("/")}>
          <ButtonIcon><PlusIcon /></ButtonIcon>
          {atLimit ? "3 event limit reached" : "+ New invite"}
        </button>
      </div>

      <div className="stack" style={{ marginTop: 24 }}>
        {atLimit && <p className="muted">Delete an existing event to create a new one.</p>}
        {error && <p className="error">{error}</p>}
        {events === null && <p className="muted">Loading your events…</p>}
        {events?.length === 0 && <p className="muted">No events yet. Claim a link to get started.</p>}
        {events?.map((e) => (
          <div key={e.slug} className="card row dashboard-event-card">
            <div className="dashboard-event-info">
              <div style={{ fontWeight: 700 }}>{e.details?.title || e.slug}</div>
              <div className="dashboard-event-meta">
                <a href={`${PUBLIC_BASE_URL}/${e.slug}`} target="_blank" rel="noreferrer">
                  {new URL(`${PUBLIC_BASE_URL}/${e.slug}`).host}/{e.slug}
                </a>
                <span aria-hidden="true">·</span>
                <span>{e.status}</span>
              </div>
            </div>
            <div className="spacer" />
            <div className="dashboard-event-actions">
              <div className="row dashboard-event-actions-primary">
                <a className="btn-ghost btn" href={`${PUBLIC_BASE_URL}/${e.slug}`} target="_blank" rel="noreferrer">
                  <ButtonIcon><ExternalLinkIcon /></ButtonIcon>
                  View
                </a>
                <button className="btn-ghost btn" onClick={() => handleCopyLink(e.slug)}>
                  <ButtonIcon><CopyIcon /></ButtonIcon>
                  {copiedSlug === e.slug ? "Copied" : "Copy link"}
                </button>
                <Link className="btn" to={`/app/edit/${e.slug}`}>
                  <ButtonIcon><PencilIcon /></ButtonIcon>
                  Edit
                </Link>
              </div>
              <div className="row dashboard-event-actions-secondary">
                <Link className="btn-ghost btn" to={`/app/responses/${e.slug}`}>
                  <ButtonIcon><ClipboardIcon /></ButtonIcon>
                  Registrations
                </Link>
                <Link className="btn-ghost btn" to={`/app/subscribers/${e.slug}`}>
                  <ButtonIcon><UsersIcon /></ButtonIcon>
                  Subscribers
                </Link>
                <button
                  className="btn-ghost btn btn-danger"
                  disabled={deletingSlug === e.slug}
                  onClick={() => handleDelete(e.slug)}
                >
                  <ButtonIcon><TrashIcon /></ButtonIcon>
                  {deletingSlug === e.slug ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
